/**
 * Login - Pantalla de inicio de sesión con estilo Koomo elegante
 * Paleta: Tonos anaranjados/salmonados con toques amarillos y blancos
 */

import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || ''

export default function Login() {
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [welcomeImage, setWelcomeImage] = useState(null)

  const { login } = useAuth()

  // Cargar imagen de bienvenida desde Wassenger (endpoint público)
  useEffect(() => {
    const loadWelcomeImage = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/crm-web/public/login-image`)
        if (response.ok) {
          const data = await response.json()
          if (data.url) {
            setWelcomeImage(data.url)
          }
        }
      } catch (err) {
        console.warn('No se pudo cargar imagen de bienvenida:', err)
      }
    }
    loadWelcomeImage()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!phone || !password) {
      setError('Ingresa usuario/teléfono y contraseña')
      return
    }

    setLoading(true)

    const result = await login(phone, password)

    if (!result.success) {
      setError(result.error)
      setLoading(false)
    }
    // Si success = true, el componente se desmonta (redirect a dashboard)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-950 via-amber-900 to-orange-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Círculos degradados - tonos cálidos */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-400/30 to-amber-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-yellow-400/20 to-orange-300/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-gradient-to-br from-amber-300/15 to-yellow-400/10 rounded-full blur-3xl"></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 bg-gradient-to-br from-orange-300/20 to-red-400/10 rounded-full blur-3xl"></div>

        {/* Grid sutil */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Contenedor principal */}
      <div className="relative z-10 w-full max-w-4xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">

        {/* Sección izquierda - Bienvenida con imagen */}
        <div className="flex-1 text-center lg:text-left">
          {/* Imagen de bienvenida */}
          <div className="mb-6 flex justify-center lg:justify-start">
            <div className="relative">
              {welcomeImage ? (
                <img
                  src={welcomeImage}
                  alt="Bienvenido a Club Almassenger"
                  className="w-48 h-48 lg:w-64 lg:h-64 object-contain drop-shadow-2xl"
                />
              ) : (
                // Placeholder mientras carga
                <div className="w-48 h-48 lg:w-64 lg:h-64 flex items-center justify-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-amber-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-orange-500/30 animate-pulse">
                    <span className="text-5xl text-white font-bold">CA</span>
                  </div>
                </div>
              )}
              {/* Resplandor detrás de la imagen */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-br from-orange-400/40 to-yellow-400/30 blur-3xl rounded-full scale-75"></div>
            </div>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            ¡Bienvenido!
          </h1>
          <p className="text-lg text-orange-100 mb-4">
            Sistema de Gestión CRM
          </p>
          <p className="text-sm text-orange-200/70 max-w-md mx-auto lg:mx-0">
            Gestiona tus conversaciones de WhatsApp, prospectos y campañas en un solo lugar.
          </p>

          {/* Stats decorativos */}
          <div className="mt-8 flex justify-center lg:justify-start gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-xs text-orange-200/60">Disponible</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-300">100%</div>
              <div className="text-xs text-orange-200/60">Seguro</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-300">CRM</div>
              <div className="text-xs text-orange-200/60">Integrado</div>
            </div>
          </div>
        </div>

        {/* Sección derecha - Formulario */}
        <div className="w-full max-w-sm">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-orange-200/20 p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/30 rotate-3 hover:rotate-0 transition-transform duration-300">
                <span className="text-2xl text-white font-bold">CA</span>
              </div>
              <h2 className="text-xl font-semibold text-white">Club Almassenger</h2>
              <p className="text-sm text-orange-200/60 mt-1">Inicia sesión para continuar</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username/Phone */}
              <div>
                <label className="block text-sm font-medium text-orange-100 mb-2">
                  Usuario o Teléfono
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-orange-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Nombre o +56912345678"
                    className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-orange-200/20 rounded-xl text-white placeholder-orange-200/40 focus:outline-none focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400/50 transition-all"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-orange-100 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-orange-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-orange-200/20 rounded-xl text-white placeholder-orange-200/40 focus:outline-none focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400/50 transition-all"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-400/30 rounded-xl p-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full relative bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3.5 rounded-xl font-medium hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-orange-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 group overflow-hidden"
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Iniciando sesión...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Iniciar Sesión
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                )}
              </button>
            </form>

            {/* Info */}
            <div className="mt-6 text-center">
              <p className="text-xs text-orange-200/50">
                Usa las credenciales de tu cuenta de soporte
              </p>
            </div>
          </div>

          {/* Powered by */}
          <p className="mt-6 text-center text-xs text-orange-300/40">
            Powered by Club Almacén
          </p>
        </div>
      </div>
    </div>
  )
}
